import { useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import { toast } from "sonner";
import { Check, WifiOff, AlertTriangle, BellRing } from "lucide-react";
import { api, errorText, fetcher } from "@/lib/api";
import { ALERT_KIND, fmtDateTimeMYT, playChime, useSoundPref } from "@/lib/traffic";
import { Seg } from "@/components/traffic/Widgets";
import { cn } from "@/lib/utils";

export default function Alerts() {
  const [scope, setScope] = useState("open");
  const { data, mutate } = useSWR(`/traffic/alerts?open=${scope === "open" ? 1 : 0}&limit=200`, fetcher, { refreshInterval: 10000 });
  const { enabled, toggle } = useSoundPref();
  const { mutate: mutateAll } = useSWRConfig();
  const ack = async (a) => { try { await api.post(`/traffic/alerts/${a.id}/ack`); toast.success("Acknowledged"); mutate(); mutateAll((key) => typeof key === "string" && key.startsWith("/traffic/alerts")); } catch (e) { toast.error(errorText(e)); } };
  const rows = data?.alerts || [];
  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="eyebrow">Alert centre</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight sm:text-4xl">Alerts</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Seg value={scope} onChange={setScope} options={[{ value: "open", label: "Open" }, { value: "all", label: "History" }]} testId="alerts-scope" />
          <button onClick={toggle} data-testid="sound-alert-toggle-page" className={cn("rounded-full border px-3 py-1.5 text-xs font-medium", enabled ? "border-emerald-300 text-emerald-700" : "text-muted-foreground")}>{enabled ? "Chime on" : "Chime muted"}</button>
          <button onClick={() => playChime(0.6)} data-testid="test-chime-button" className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium hover:bg-secondary"><BellRing className="h-3.5 w-3.5" /> Test chime</button>
        </div>
      </div>
      <section className="surface overflow-hidden" data-testid="alerts-table">
        <ul className="divide-y divide-border/60">
          {rows.map((a) => (
            <li key={a.id} className="flex items-center gap-4 px-6 py-3" data-testid={`alert-row-${a.id}`}>
              <span className={cn("rounded-xl p-2", a.severity === "critical" ? "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300")}>
                {a.kind.includes("offline") ? <WifiOff className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-sm"><span className="font-semibold">{ALERT_KIND[a.kind] || a.kind}</span><span className="text-muted-foreground">{a.site}{a.dir ? ` · ${a.dir}` : ""}</span>
                  {a.closed_at ? <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">resolved</span> : a.acked_at ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-emerald-700">acknowledged · still active</span> : null}</div>
                <div className="truncate text-xs text-muted-foreground">{a.message}</div>
              </div>
              <div className="num hidden text-right text-xs text-muted-foreground sm:block">
                <div>opened {fmtDateTimeMYT(a.opened_at)} MYT</div>
                {a.closed_at && <div>closed {fmtDateTimeMYT(a.closed_at)} MYT</div>}
                {a.acked_at && <div className="text-emerald-600">ack by {a.acked_by}</div>}
              </div>
              {!a.acked_at && <button onClick={() => ack(a)} data-testid={`acknowledge-alert-btn-${a.id}`} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"><Check className="h-3.5 w-3.5" /> Acknowledge</button>}
            </li>
          ))}
          {!rows.length && <li className="px-6 py-12 text-center text-sm text-muted-foreground" data-testid="alerts-empty">{scope === "open" ? "No open alerts — all radars reporting and traffic flowing." : "No alert history yet."}</li>}
        </ul>
      </section>
    </div>
  );
}
